package com.veldekar.components.metadata

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLLinkElement
import org.w3c.dom.HTMLMetaElement

private const val DEFAULT_OG_IMAGE = "images/veldekarMapPreview.webp"
private const val DEFAULT_OG_IMAGE_WIDTH = 1200
private const val DEFAULT_OG_IMAGE_HEIGHT = 630
private const val DEFAULT_OG_IMAGE_ALT = "Hand-drawn fantasy world map of Veldekar"

@Suppress("LongParameterList")
fun updateOpenGraphMetadata(
    title: String?,
    description: String?,
    image: String? = null,
    imageWidth: Int? = null,
    imageHeight: Int? = null,
    imageAlt: String? = null,
    aiHint: String? = null,
) {
    val head = document.head ?: return

    fun updateMeta(property: String, content: String?) {
        if (content != null) {
            val meta = head.querySelector("meta[property='$property']").unsafeCast<HTMLMetaElement?>()
                ?: document.createElement("meta").unsafeCast<HTMLMetaElement>().apply {
                    setAttribute("property", property)
                    head.appendChild(this)
                }
            meta.setAttribute("content", content)
        } else {
            head.querySelector("meta[property='$property']")?.let { head.removeChild(it) }
        }
    }

    fun updateMetaName(name: String, content: String?) {
        if (content != null) {
            val meta = head.querySelector("meta[name='$name']").unsafeCast<HTMLMetaElement?>()
                ?: document.createElement("meta").unsafeCast<HTMLMetaElement>().apply {
                    setAttribute("name", name)
                    head.appendChild(this)
                }
            meta.setAttribute("content", content)
        } else {
            head.querySelector("meta[name='$name']")?.let { head.removeChild(it) }
        }
    }

    val canonicalUrl = "https://veldekar.com${window.location.pathname}"

    // Canonical link tag
    val link = head.querySelector("link[rel='canonical']").unsafeCast<HTMLLinkElement?>()
        ?: document.createElement("link").unsafeCast<HTMLLinkElement>().apply {
            setAttribute("rel", "canonical")
            head.appendChild(this)
        }
    link.setAttribute("href", canonicalUrl)

    updateMeta("og:title", title)
    updateMeta("og:description", description)
    updateMeta("og:url", canonicalUrl)
    updateMeta("og:site_name", "Veldekar")
    updateMeta("og:type", "website")
    updateMetaName("description", description ?: "Veldekar")
    updateMetaName("ai-hint", aiHint)

    updateMeta("twitter:title", title)
    updateMeta("twitter:description", description)

    val effectiveImage = if (!image.isNullOrBlank()) image else DEFAULT_OG_IMAGE
    val effectiveWidth = if (!image.isNullOrBlank()) imageWidth else DEFAULT_OG_IMAGE_WIDTH
    val effectiveHeight = if (!image.isNullOrBlank()) imageHeight else DEFAULT_OG_IMAGE_HEIGHT
    val effectiveAlt = if (!image.isNullOrBlank()) imageAlt else DEFAULT_OG_IMAGE_ALT

    val cleanImage = effectiveImage.removePrefix("/")
    val absoluteImageUrl = "https://veldekar.com/$cleanImage"
    // OpenGraph requires absolute URLs with the production domain
    updateMeta("og:image", absoluteImageUrl)
    updateMeta("og:image:width", effectiveWidth?.toString())
    updateMeta("og:image:height", effectiveHeight?.toString())
    updateMeta("og:image:alt", effectiveAlt)

    // Twitter tags often help Discord decide to show a large image card
    updateMeta("twitter:card", "summary_large_image")
    updateMeta("twitter:image", absoluteImageUrl)
}
